// Cache of "real" preview numbers for the SF → Half Moon Bay example shown
// on the landing page. Populated by Index.tsx the first time the example
// route finishes scoring, then read by Onboarding so the comparison cards
// display real Pleasant scores, ETAs, distances, and reasons instead of
// placeholders.

import type { ClassifiedRoute } from "@/utils/scoring";

// Bump this whenever the snapshot shape OR the example coordinates change —
// older cache entries that don't match will be auto-invalidated on read.
const SCHEMA_VERSION = 6;
const KEY = `pleasant.examplePreview.v${SCHEMA_VERSION}`;
// Legacy keys we should clear on load so they don't shadow the current key
// or pile up in localStorage. v5 bumped to 6 to evict stale snapshots that
// overstated the example's time gap (e.g. 35 vs 42 min) before the ETAs were
// reconciled with the live ~40-vs-43 result.
const LEGACY_KEYS = ["pleasant.examplePreview", "pleasant.examplePreview.v2", "pleasant.examplePreview.v3", "pleasant.examplePreview.v4", "pleasant.examplePreview.v5"];
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

// SF Embarcadero → Half Moon Bay. Match within ~1 km on each end.
// IMPORTANT: if you change these coordinates, also bump SCHEMA_VERSION above
// (or simply rely on the in-snapshot coord check below to invalidate stale
// entries automatically). The coords are baked into each snapshot so reading
// a snapshot from a previous coordinate pair returns null.
const EXAMPLE_START: [number, number] = [-122.3933, 37.7955];
const EXAMPLE_END:   [number, number] = [-122.4286, 37.4636];
const TOL = 0.012; // ≈1.3 km lat/lng — generous; example uses fixed coords.

export interface ExamplePreviewSnapshot {
  savedAt: string;
  schemaVersion: number;
  /** The exact endpoints used to compute this snapshot. Reading a snapshot
   *  whose endpoints don't match the current EXAMPLE_START/END returns null
   *  so the next render triggers a fresh re-scoring. */
  start: [number, number];
  end: [number, number];
  fastest: { durationS: number; distanceM: number };
  pleasant: {
    durationS: number;
    distanceM: number;
    pleasantScore: number;        // 0–100
    reasons: string[];            // e.g. ["Pacific Ocean coastline", "3 state parks", "quiet roads"]
  };
}

const near = (a: [number, number], b: [number, number]): boolean =>
  Math.abs(a[0] - b[0]) < TOL && Math.abs(a[1] - b[1]) < TOL;

export function isExampleTrip(
  startCoords: [number, number] | undefined,
  destCoords: [number, number] | undefined,
): boolean {
  if (!startCoords || !destCoords) return false;
  return near(startCoords, EXAMPLE_START) && near(destCoords, EXAMPLE_END);
}

function reasonsFor(r: ClassifiedRoute): string[] {
  const f = r.features ?? ({} as ClassifiedRoute["features"]);
  const out: string[] = [];

  // Coastline / waterside lead reason.
  const coast = (f.waterAdjacency ?? []).find((w) => w.kind === "coastline");
  if (coast?.name) out.push(`${coast.name} coastline`);
  else if ((f.coastlineAdjacentMeters ?? 0) > 500) out.push("Ocean coastline");
  else if ((f.waterNames ?? []).length > 0) out.push(`Along ${f.waterNames![0]}`);

  // Parks / nature.
  const parkCount = (f.parkNames?.length ?? 0) || (f.parks ?? 0);
  if (parkCount > 0) {
    const lead = f.parkNames?.[0];
    out.push(lead ? `${lead}${parkCount > 1 ? ` +${parkCount - 1} parks` : ""}` : `${parkCount} parks`);
  }

  // Quiet streets — derived from low highway / low arterial share.
  const hwy = f.highwayPct ?? 0;
  const major = f.majorRoadPct ?? 0;
  if (hwy < 25 && major < 25) out.push("Quiet roads");

  // Historic / landmarks as a tertiary nicety.
  if ((f.historicNames?.length ?? 0) > 0) out.push(f.historicNames![0]);
  else if ((f.landmarks ?? 0) > 0 && out.length < 3) out.push(`${f.landmarks} landmarks`);

  return out.slice(0, 3);
}

/** Persist a snapshot if `scored` includes both a fastest and pleasant route. */
export function maybeSaveExamplePreview(scored: ClassifiedRoute[]): void {
  try {
    const fastest = scored.find((r) => r.classification === "fastest") ?? scored[0];
    const pleasant =
      scored.find((r) => r.classification === "pleasant") ??
      scored.find((r) => r.classification === "scenic") ??
      scored[scored.length - 1];
    if (!fastest || !pleasant) return;

    const snap: ExamplePreviewSnapshot = {
      savedAt: new Date().toISOString(),
      schemaVersion: SCHEMA_VERSION,
      start: EXAMPLE_START,
      end: EXAMPLE_END,
      fastest: {
        durationS: fastest.route.duration,
        distanceM: fastest.route.distance,
      },
      pleasant: {
        durationS: pleasant.route.duration,
        distanceM: pleasant.route.distance,
        pleasantScore: Math.round(pleasant.scores?.pleasant ?? 0),
        reasons: reasonsFor(pleasant),
      },
    };
    localStorage.setItem(KEY, JSON.stringify(snap));
  } catch {
    /* best-effort */
  }
}

/** Wipe any cached example-preview snapshots (current + legacy keys). Call
 *  this after changing the example coordinates / scoring rules to force a
 *  fresh recompute on the next render. Safe to call repeatedly. */
export function clearExamplePreview(): void {
  try {
    localStorage.removeItem(KEY);
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);
  } catch {
    /* best-effort */
  }
}

/** Returns the current example endpoints so callers (Index.tsx, Onboarding,
 *  dev tools) can stay in sync with this module's source of truth. */
export function getExampleEndpoints(): {
  start: [number, number];
  end: [number, number];
} {
  return { start: EXAMPLE_START, end: EXAMPLE_END };
}

export function loadExamplePreview(): ExamplePreviewSnapshot | null {
  try {
    // Sweep stale legacy keys so they never resurface.
    for (const k of LEGACY_KEYS) {
      if (localStorage.getItem(k) != null) localStorage.removeItem(k);
    }

    // Honor a one-shot refresh signal: ?refreshExample=1 in the URL OR a
    // localStorage flag set by `window.__refreshExamplePreview()`. Both clear
    // the cache once and then remove the signal, so the next page load
    // recomputes against the current scoring + coords.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const flag = url.searchParams.get("refreshExample");
      const lsFlag = localStorage.getItem("pleasant.examplePreview.refresh");
      if (flag === "1" || lsFlag === "1") {
        clearExamplePreview();
        try { localStorage.removeItem("pleasant.examplePreview.refresh"); } catch { /* ignore */ }
        if (flag === "1") {
          url.searchParams.delete("refreshExample");
          try {
            window.history.replaceState(null, "", url.toString());
          } catch { /* ignore */ }
        }
        return null;
      }
    }

    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as ExamplePreviewSnapshot;
    if (!snap?.savedAt || !snap.pleasant || !snap.fastest) return null;
    if (snap.schemaVersion !== SCHEMA_VERSION) return null;
    // Endpoint guard: if the cached snapshot was computed against different
    // coordinates than the ones currently configured, ignore it so the next
    // scoring run repopulates the cache for the new trip.
    if (
      !Array.isArray(snap.start) || !Array.isArray(snap.end) ||
      !near(snap.start as [number, number], EXAMPLE_START) ||
      !near(snap.end as [number, number], EXAMPLE_END)
    ) {
      return null;
    }
    const age = Date.now() - new Date(snap.savedAt).getTime();
    if (Number.isFinite(age) && age > MAX_AGE_MS) return null;
    return snap;
  } catch {
    return null;
  }
}

// Expose a tiny dev helper on window so you can refresh the cache from the
// browser console: `__refreshExamplePreview()` then reload. No-op on SSR.
if (typeof window !== "undefined") {
  (window as unknown as { __refreshExamplePreview?: () => void }).__refreshExamplePreview = () => {
    clearExamplePreview();
    try { localStorage.setItem("pleasant.examplePreview.refresh", "1"); } catch { /* ignore */ }
     
    console.info("[examplePreview] cache cleared — reload the page to recompute.");
  };
}
